import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, TextField, Typography, Box, Grid, Paper, Dialog, DialogActions, DialogContent, DialogTitle, Alert, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import config from './config';
import { apiCall } from './api';
import { MapContainer, Marker, TileLayer, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const DEFAULT_ABOUT_HTML = `<p>Map250 is a resource developed by Whose Land, an educational nonprofit that responds to the histories and legacies of land displacement and dispossession in America through collaborative community-based humanities projects.</p>
<p>The Map250 platform is designed with collaboration and community input in mind. Currently being used in over a dozen classrooms, colleges, tribal nations and community group settings, the map invites users to share place-based stories, memories, photographs and other historical memorabilia by location and historical era.</p>
<p>The resulting historical layering of stories over single sites allows users to discover changes in the land and in the built environment and to discover the impact on the resident people and ecology in each place.</p>
<p><strong>Map250 is interactive!</strong></p>
<p>You can add and share place-based stories, comment on locations already featured on the map, or respond to the entries and input of other visitors. You can also ask questions about locations and other posts. We'd love your input!</p>
<p><em>Visitor posts will be reviewed and published within 48 hours.</em></p>`;

const QUILL_MODULES = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
    ],
};


const crosshairIcon = new L.Icon({
    iconUrl: '/1530077_crosshair_direction_location_locator_navigation_icon.png',
    iconSize: [50, 50],
    iconAnchor: [25, 25],
});


function AdminProjectManager({ user, logout, handleLogin, loading, isGlobalAdmin, userProjectIds }) {
    const [projectData, setProjectData] = useState({ projectid: '', project_name: '', project_url_slug: '', description: '', center_lat: null, center_lng: null, zoom_level: 13, logo_url: '' });
    const [projects, setProjects] = useState([]); // Initialize as an empty array
    const [newProject, setNewProject] = useState({ projectid: '', project_name: '', project_url_slug: '', description: '', logo_url: '', site_type: 'default', exhibition_title: '', exhibition_message: '', exhibition_about_html: '' });
    const [uploadingImage, setUploadingImage] = useState(false);
    const [editProject, setEditProject] = useState(null); // State for the project being edited
    const [openEditDialog, setOpenEditDialog] = useState(false); // Dialog state
    const [isEditingProjectId, setIsEditingProjectId] = useState(false); // State to track projectid editing
    const [showWarning, setShowWarning] = useState(false); // State for showing warning
    const [errorMessage, setErrorMessage] = useState(''); // State for error messages
    const [mapCenter, setMapCenter] = useState([43.115885, -77.512659]);
    const [zoom, setZoom] = useState(13);
    const [openMapModal, setOpenMapModal] = useState(false);
    const [selectedProjectsid, setSelectedProjectsid] = useState(null);

    // When the Edit Map Center button is clicked, store the selected project in state
    const handleOpenMapCenterModal = (projectsid) => {
        setSelectedProjectsid(projectsid); // Store the selected project in state
        setOpenMapModal(true); // Open the map modal
    };

    // Function to handle closing the modal
    const handleCloseMapModal = () => {
        setOpenMapModal(false);
    };
    const handleMapClick = (e) => {
        setMapCenter([e.latlng.lat, e.latlng.lng]);
        setProjectData({ ...projectData, center_lat: e.latlng.lat, center_lng: e.latlng.lng });
    };

    // Update zoom level when map zoom changes
    const handleZoom = (map) => {
        setZoom(map.getZoom());
        setProjectData({ ...projectData, zoom_level: map.getZoom() });
    };

    // Fetch Projects on component load
    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = () => {
        axios.post(config.getApiUrl('adminProjectsManager.php'), { action: 'get' })
            .then((response) => {
                if (response.data.success && Array.isArray(response.data.projects)) {
                    setProjects(response.data.projects);
                } else {
                    console.error('Expected an array but got:', response.data);
                    setProjects([]); // Reset to empty if invalid response
                }
            })
            .catch((error) => console.error('Error fetching projects:', error));
    };

    const handleAddProject = async () => {
        // Validate form fields before submitting
        if (!newProject.projectid || !newProject.project_name || !newProject.project_url_slug || !newProject.description) {
            setErrorMessage('All fields are required');
            return;
        }

        // Ensure map center and zoom level are also present
        if (!mapCenter[0] || !mapCenter[1] || !zoom) {
            setErrorMessage('Map center and zoom level must be set');
            return;
        }

        // apiCall() attaches the Firebase ID token. adminProjectsManager.php's
        // 'add' action now requires require_global_admin (auth refactor #59);
        // the previous raw axios call sent no token, so every create silently
        // 401'd and the .catch swallowed it — "Submit did nothing" (BUG-D).
        try {
            const data = await apiCall('adminProjectsManager.php', {
                method: 'POST',
                body: {
                    action: 'add',
                    projectid: newProject.projectid,
                    project_name: newProject.project_name,
                    project_url_slug: newProject.project_url_slug,
                    description: newProject.description,
                    logo_url: newProject.logo_url,
                    center_lat: mapCenter[0],
                    center_lng: mapCenter[1],
                    zoom_level: zoom,
                    site_type: newProject.site_type || 'default',
                    exhibition_title: newProject.exhibition_title || null,
                    exhibition_message: newProject.exhibition_message || null,
                    exhibition_about_html: newProject.exhibition_about_html || null,
                },
            });
            if (data && data.success) {
                fetchProjects();
                setNewProject({ projectid: '', project_name: '', project_url_slug: '', description: '', logo_url: '', site_type: 'default', exhibition_title: '', exhibition_message: '', exhibition_about_html: '' });
                setErrorMessage('');
            } else {
                setErrorMessage((data && data.error) || 'Error adding project');
            }
        } catch (error) {
            setErrorMessage(error?.body?.error || error?.message || 'Error adding project');
        }
    };



    const handleDeleteProject = async (projectsid) => {
        try {
            const data = await apiCall('adminProjectsManager.php', {
                method: 'POST',
                body: { action: 'delete', projectsid },
            });
            if (data && data.success) {
                setProjects(projects.filter(project => project.projectsid !== projectsid));
            } else {
                setErrorMessage((data && data.error) || 'Failed to delete the project');
            }
        } catch (error) {
            setErrorMessage(error?.body?.error || error?.message || 'Failed to delete the project');
        }
    };

    const handleOpenEditDialog = (project) => {
        setEditProject(project);
        setIsEditingProjectId(false); // Reset projectid editing state
        setOpenEditDialog(true);
    };

    const handleExhibitionImageUpload = async (projectsid, file) => {
        if (!file || !projectsid) return;
        setUploadingImage(true);
        const formData = new FormData();
        formData.append('projectsid', projectsid);
        formData.append('image', file);
        try {
            const response = await axios.post(config.getApiUrl('uploadProjectImage.php'), formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (response.data.success) {
                if (editProject) {
                    setEditProject({ ...editProject, exhibition_bg_image: response.data.image_url });
                }
                fetchProjects();
            } else {
                setErrorMessage(response.data.message || 'Image upload failed');
            }
        } catch (error) {
            console.error('Error uploading exhibition image:', error);
            setErrorMessage('Image upload failed');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleEditProject = async () => {
        try {
            const data = await apiCall('adminProjectsManager.php', {
                method: 'POST',
                body: {
                    action: 'update',
                    projectsid: editProject.projectsid,
                    projectid: editProject.projectid,
                    project_name: editProject.project_name,
                    project_url_slug: editProject.project_url_slug,
                    description: editProject.description,
                    logo_url: editProject.logo_url,
                    site_type: editProject.site_type || 'default',
                    exhibition_title: editProject.exhibition_title || null,
                    exhibition_message: editProject.exhibition_message || null,
                    exhibition_about_html: editProject.exhibition_about_html || null,
                },
            });
            if (data && data.success) {
                setProjects(projects.map(project => project.projectsid === editProject.projectsid ? editProject : project));
                setOpenEditDialog(false);
            } else {
                setErrorMessage((data && data.error) || 'Failed to update the project');
            }
        } catch (error) {
            setErrorMessage(error?.body?.error || error?.message || 'Failed to update the project');
        }
    };

    // Modify the handleMapCenterEdit function to accept the selected project.
    // Goes through apiCall() so the Firebase token is attached — the
    // update_map_center action is gated by require_project_admin (#59).
    const handleMapCenterEdit = async () => {
        try {
            const result = await apiCall('adminProjectsManager.php', {
                method: 'POST',
                body: {
                    action: 'update_map_center',
                    projectsid: selectedProjectsid,
                    center_lat: mapCenter[0],
                    center_lng: mapCenter[1],
                    zoom_level: zoom,
                },
            });

            if (result && result.success) {
                alert('Map center and zoom level updated successfully!');
                handleCloseMapModal(); // Close the modal after successful update
            } else {
                alert((result && result.error) || 'Failed to update map center and zoom level.');
            }
        } catch (error) {
            alert(error?.body?.error || 'An error occurred while updating the map center and zoom level.');
        }
    };


    const handleEditFieldChange = (field, value) => {
        setEditProject({ ...editProject, [field]: value });
    };

    const handleEditProjectId = () => {
        setIsEditingProjectId(true); // Enable projectid editing
        setShowWarning(true); // Show the warning message
    };


    return (
        <Box sx={{ width: '100%', height: '100%' }}>
            {/* Project Management Section */}
            <Grid sx={{ padding: 5 }}>
                <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', marginBottom: 4 }}>
                    Project Manager
                </Typography>

                {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Paper elevation={3} sx={{ padding: 2 }}>
                            <Grid container spacing={2}>
                                {isGlobalAdmin && (<>
                                <Grid item xs={12} md={4}>
                                    <Box>
                                        <Paper elevation={3} sx={{ padding: 3 }}>
                                            <Typography variant="h6" sx={{ marginBottom: 2 }}>Add New Project</Typography>
                                            <TextField
                                                label="Project ID"
                                                value={newProject.projectid}
                                                onChange={(e) => setNewProject({ ...newProject, projectid: e.target.value })}
                                                fullWidth
                                                margin="normal"
                                            />
                                            <TextField
                                                label="Project Name"
                                                value={newProject.project_name}
                                                onChange={(e) => setNewProject({ ...newProject, project_name: e.target.value })}
                                                fullWidth
                                                margin="normal"
                                            />
                                            <TextField
                                                label="Project URL Slug"
                                                value={newProject.project_url_slug}
                                                onChange={(e) => setNewProject({ ...newProject, project_url_slug: e.target.value })}
                                                fullWidth
                                                margin="normal"
                                            />
                                            <TextField
                                                label="Description"
                                                value={newProject.description}
                                                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                                                fullWidth
                                                margin="normal"
                                            />
                                            <TextField
                                                label="Project Logo URL"
                                                value={newProject.logo_url}
                                                onChange={(e) => setNewProject({ ...newProject, logo_url: e.target.value })}
                                                fullWidth
                                                margin="normal"
                                            />
                                            <FormControl fullWidth margin="normal">
                                                <InputLabel>Site Type</InputLabel>
                                                <Select
                                                    value={newProject.site_type || 'default'}
                                                    label="Site Type"
                                                    onChange={(e) => setNewProject({ ...newProject, site_type: e.target.value })}
                                                >
                                                    <MenuItem value="default">Default</MenuItem>
                                                    <MenuItem value="city">City</MenuItem>
                                                    <MenuItem value="exhibition">Exhibition</MenuItem>
                                                </Select>
                                            </FormControl>
                                            {newProject.site_type === 'exhibition' && (
                                                <>
                                                    <TextField
                                                        label="Screensaver Title"
                                                        value={newProject.exhibition_title}
                                                        onChange={(e) => setNewProject({ ...newProject, exhibition_title: e.target.value })}
                                                        fullWidth
                                                        margin="normal"
                                                        placeholder="e.g. Enter the Interactive History Map"
                                                    />
                                                    <TextField
                                                        label="Screensaver Message"
                                                        value={newProject.exhibition_message}
                                                        onChange={(e) => setNewProject({ ...newProject, exhibition_message: e.target.value })}
                                                        fullWidth
                                                        margin="normal"
                                                        multiline
                                                        minRows={2}
                                                        placeholder="e.g. Step into stories of place, memory, and community."
                                                    />
                                                    <Box sx={{ mt: 2, mb: 1 }}>
                                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>About Page Content</Typography>
                                                        <ReactQuill
                                                            theme="snow"
                                                            value={newProject.exhibition_about_html || DEFAULT_ABOUT_HTML}
                                                            onChange={(value) => setNewProject({ ...newProject, exhibition_about_html: value })}
                                                            modules={QUILL_MODULES}
                                                            style={{ minHeight: 200 }}
                                                        />
                                                    </Box>
                                                </>
                                            )}
                                            <Button variant="contained" color="primary" onClick={handleAddProject} fullWidth sx={{ marginTop: 2 }}>
                                                Add Project
                                            </Button>
                                        </Paper>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <Paper elevation={3} sx={{ padding: 2 }}>
                                        <MapContainer center={mapCenter} zoom={zoom} style={{ height: '450px', cursor: 'crosshair' }} whenCreated={handleZoom}>
                                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                            <Marker position={mapCenter} icon={crosshairIcon} />
                                            <MapEvents onClick={handleMapClick} onZoom={handleZoom} />
                                        </MapContainer>
                                        <Typography variant="body2">Click on the map to set the center of the project.</Typography>
                                        <Typography variant="h6">Lat: {mapCenter[0]}, Lng: {mapCenter[1]}, ZoomLevel: {zoom}</Typography>
                                    </Paper>
                                </Grid>
                                </>)}
                            </Grid>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Paper elevation={3} sx={{ padding: 3 }}>
                            <Typography variant="h6" sx={{ marginBottom: 2 }}>Existing Projects</Typography>
                            {projects && Array.isArray(projects) && projects.length > 0 ? (
                                projects
                                    .filter(project => isGlobalAdmin || userProjectIds.includes(project.projectid))
                                    .map((project) => (
                                    <Grid container key={project.projectsid} spacing={2} sx={{ marginTop: 2 }}>
                                        <Grid item xs={6} sm={6} md={6} lg={4}>
                                            <Typography>{project.project_name || 'Unnamed Project'}</Typography> {/* Defensive check here */}
                                            <Typography>{project.projectid || 'N/A'}</Typography> {/* Defensive check here */}
                                        </Grid>
                                        <Grid item xs={6} sm={6} md={6} lg={4}>
                                            <Typography>{project.description || 'N/A'}</Typography> {/* Defensive check here */}
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={12} lg={4}>
                                            <Button variant="contained" color="primary" onClick={() => handleOpenEditDialog(project)}>
                                                Edit
                                            </Button>
                                            <Button variant="contained" color="primary" onClick={() => handleOpenMapCenterModal(project.projectsid)} sx={{ marginLeft: 2 }}>
                                                Edit Map Center
                                            </Button>
                                            {isGlobalAdmin && (
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                onClick={() => {
                                                    if (window.confirm("Are you sure you want to delete this project?")) {
                                                        handleDeleteProject(project.projectsid);
                                                    }
                                                }}
                                                sx={{ marginLeft: 2 }}
                                            >
                                                Delete
                                            </Button>
                                            )}
                                        </Grid>
                                    </Grid>
                                ))
                            ) : (
                                <Typography>No projects found or available.</Typography>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </Grid>

            {/* Edit Project Dialog */}
            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)}>
                <DialogTitle>Edit Project</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Project ID"
                        value={editProject?.projectid || ''}
                        onChange={(e) => handleEditFieldChange('projectid', e.target.value)}
                        fullWidth
                        margin="normal"
                        disabled={!isEditingProjectId} // Disable editing unless enabled
                    />
                    {showWarning && <Alert severity="warning">Warning: Editing the Project ID can cause significant issues. Proceed with caution.</Alert>}
                    {!isEditingProjectId && (
                        <Button onClick={handleEditProjectId} variant="contained" color="secondary" sx={{ marginTop: 2 }}>
                            Edit Project ID
                        </Button>
                    )}
                    <TextField
                        label="Project Name"
                        value={editProject?.project_name || ''}
                        onChange={(e) => handleEditFieldChange('project_name', e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="Project URL Slug"
                        value={editProject?.project_url_slug || ''}
                        onChange={(e) => handleEditFieldChange('project_url_slug', e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="Description"
                        value={editProject?.description || ''}
                        onChange={(e) => handleEditFieldChange('description', e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="Project Logo URL"
                        value={editProject?.logo_url || ''}
                        onChange={(e) => handleEditFieldChange('logo_url', e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Site Type</InputLabel>
                        <Select
                            value={editProject?.site_type || 'default'}
                            label="Site Type"
                            onChange={(e) => handleEditFieldChange('site_type', e.target.value)}
                        >
                            <MenuItem value="default">Default</MenuItem>
                            <MenuItem value="city">City</MenuItem>
                            <MenuItem value="exhibition">Exhibition</MenuItem>
                        </Select>
                    </FormControl>
                    {editProject?.site_type === 'exhibition' && (
                        <>
                            <TextField
                                label="Screensaver Title"
                                value={editProject?.exhibition_title || ''}
                                onChange={(e) => handleEditFieldChange('exhibition_title', e.target.value)}
                                fullWidth
                                margin="normal"
                                placeholder="e.g. Enter the Interactive History Map"
                            />
                            <TextField
                                label="Screensaver Message"
                                value={editProject?.exhibition_message || ''}
                                onChange={(e) => handleEditFieldChange('exhibition_message', e.target.value)}
                                fullWidth
                                margin="normal"
                                multiline
                                minRows={2}
                                placeholder="e.g. Step into stories of place, memory, and community."
                            />
                            <Box sx={{ mt: 2, mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Screensaver Background Image</Typography>
                                {editProject?.exhibition_bg_image && (
                                    <Box sx={{ mb: 1 }}>
                                        <img
                                            src={editProject.exhibition_bg_image}
                                            alt="Screensaver background"
                                            style={{ maxWidth: '100%', maxHeight: 150, objectFit: 'cover', borderRadius: 4 }}
                                        />
                                    </Box>
                                )}
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<CloudUploadIcon />}
                                    disabled={uploadingImage}
                                >
                                    {uploadingImage ? 'Uploading...' : 'Upload Image'}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={(e) => {
                                            if (e.target.files[0]) {
                                                handleExhibitionImageUpload(editProject.projectsid, e.target.files[0]);
                                            }
                                        }}
                                    />
                                </Button>
                            </Box>
                            <Box sx={{ mt: 2, mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>About Page Content</Typography>
                                <ReactQuill
                                    theme="snow"
                                    value={editProject?.exhibition_about_html || DEFAULT_ABOUT_HTML}
                                    onChange={(value) => handleEditFieldChange('exhibition_about_html', value)}
                                    modules={QUILL_MODULES}
                                    style={{ minHeight: 200 }}
                                />
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
                    <Button onClick={handleEditProject} color="primary" variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={openMapModal} onClose={handleCloseMapModal} maxWidth="md" fullWidth>
                <DialogTitle>Edit Map Center</DialogTitle>
                <DialogContent>
                    <Paper elevation={3} sx={{ padding: 2 }}>
                        <MapContainer center={mapCenter} zoom={zoom} style={{ height: '400px', cursor: 'crosshair' }} whenCreated={handleZoom}>
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                            <Marker position={mapCenter} icon={crosshairIcon} />
                            <MapEvents onClick={handleMapClick} onZoom={handleZoom} />
                        </MapContainer>
                        <Typography variant="body2">Click on the map to set the center of the project.</Typography>
                        <Typography variant="h6">Lat: {mapCenter[0]}, Lng: {mapCenter[1]}, ZoomLevel: {zoom}</Typography>
                    </Paper>
                </DialogContent>
                <Button onClick={handleMapCenterEdit} variant="contained" color="primary">Submit New Map Center</Button>
                <Button onClick={handleCloseMapModal}>Close</Button>
            </Dialog>
        </Box>
    );
}
function MapEvents({ onClick, onZoom }) {
    useMapEvents({
        click: onClick,
        zoomend: (e) => onZoom(e.target), // Update zoom when zoom changes
    });
    return null;
}
export default AdminProjectManager;
